// Delete: what the key, the Edit menu, the toolbar button and ERASE all run —
// a room's sub-selected feature first, else the live space's selection (the
// model's, a sheet's), and everything that went with what was deleted
// (dimensions, ceiling plans). docs/claude/selection.md.
import {
  removeSupport,
  selectedSupportNow,
} from './benchSupportTool';
import { removeRoomBench } from './benchPanel';
import {
  flashConnectionsDeleted,
  pruneConnectionsTo,
} from './connectionUi';
import {
  deleteConstraint,
  selectedConstraint,
} from './constraintUi';
import {
  invalidate,
  leaveEdit,
  pushHistory,
  renderProperties,
  state,
} from './core';
import { roomDoors } from '../door';
import { removeObjects } from '../drawingObjects';
import {
  normalizeGroups,
  pruneRelations,
} from '../groups';
import { removeHeaterWall } from './heaterWalls';
import { pruneLayerOrders } from '../layers';
import { pickedLightsNow } from './lightTools';
import {
  removeFixture,
  selectedFixtureNow,
} from './roomFixtures';
import {
  deleteTargets,
  dropFeature,
  featureOf,
} from '../selection';
import { deletableSheetObjects } from '../sheetObjects';
import { titleEditor } from './titleEditor';
import { deleteSection3d } from './view3d';
import {
  bgImages,
  bumpBackground,
  closeTextEditor,
  deleteSelectedFloor,
  deleteViewTitle,
  deselectAll,
  editedBg,
  editModeDelete,
  editModeDeletes,
  glassPanesOf,
  hasBgGeom,
  OBJECTS,
  openingsOf,
  recarveRoomsAfterRemoving,
  regenerateSauna,
  removeInnerWall,
  removeWallFeature,
  selectedDoor,
  selectedFloor,
  selectedGlassPanes,
  selectedObjectIds,
  selectedOpenings,
  selectedWallFeature,
  SPACES,
} from '../main';
import {
  activeSheet,
  schemSelSet,
  SHEET_OBJECTS,
} from './sheetSpace';
import { deleteSchematicSelected } from './sheetSchematic';
import { anchorObjectId } from '../anchors';

/** Delete every object in a heterogeneous multi-selection at once, with the same
 *  cleanup each single-kind delete does (room fixtures, connections, dimensions,
 *  bench rebuilds). */
/** Delete these objects, of any kind, with the cascades their own deletes run:
 *  the connections and dimensions on them, a room's fixtures, relations, groups
 *  left too small, the benches they carved. The caller records the undo step. */
export function deleteObjectsById(ids: ReadonlySet<string>): void {
  removeObjectsById(ids);
  deselectAll();
}

/** THE delete: the objects `ids` name go, of any kind, and with them what hangs on
 *  them — what each kind's adapter says goes with it (a room's fixtures and
 *  fibre-kit projectors, its ceiling plans: drawingObjects.ts `removeObjects`),
 *  the connections glued to any of them (the bar says how many), the dimensions
 *  snapped to them, relations left to nothing, groups left with one member, the
 *  rooms they carved, the layers' orders. Every path that removes an object goes
 *  through here (a unit case holds main.ts to it). No history, no selection: the
 *  caller's. */
export function removeObjectsById(ids: ReadonlySet<string>): void {
  const d = state.drawing;
  if (state.editingTextId && ids.has(state.editingTextId)) closeTextEditor(false);
  const removed = removeObjects(d, ids, OBJECTS);
  const linked = removed.map((it) => it.obj.id);
  flashConnectionsDeleted(pruneConnectionsTo(linked));
  pruneDimensionsTo(new Set(linked));
  pruneCeilingPlans();
  pruneRelations(d.instances); // a part left related to nothing keeps no relation
  normalizeGroups(d); // a group left with one member dissolves
  recarveRoomsAfterRemoving(removed.flatMap((it) => (it.kind === 'instance' ? [it.obj] : [])));
  pruneLayerOrders(d);
}

/** Delete `ids` as one undo step (the selection's Delete), leaving nothing selected. */
function deleteIds(ids: Iterable<string>): void {
  pushHistory();
  leaveEdit('shape');
  deleteObjectsById(new Set(ids));
  renderProperties();
  invalidate();
}

export function deleteAllSelected(): void {
  deleteIds([...selectedObjectIds()]);
}

/** Remove the sub-selected room feature — a door, or the selected glazed pane(s)
 *  or opening(s) — and report whether there was one. Delete must take the feature,
 *  never the room it sits in; this lives in `deleteSelected` so the key, the Edit
 *  menu, the toolbar button and the ERASE command all honour it. */
function deleteSelectedRoomFeature(): boolean {
  const door = selectedDoor();
  const wf = selectedWallFeature();
  const sup = selectedSupportNow();
  const fx = selectedFixtureNow();
  const many = pickedLightsNow();
  if (many) {
    pushHistory();
    for (const l of many.lights) removeFixture(many.inst, l.id);
    dropFeature(state, 'fixture');
  } else if (fx) {
    pushHistory();
    removeFixture(fx.inst, fx.id);
  } else if (sup) {
    pushHistory();
    removeSupport(sup.inst, sup.sup.id, featureOf(state, 'support')?.run);
  } else if (wf) {
    pushHistory();
    removeWallFeature(wf.wall, wf.f);
  } else if (door) {
    pushHistory();
    door.inst.doors = roomDoors(door.inst).filter((d) => d.id !== door.door.id);
    dropFeature(state, 'door', 'glass');
    regenerateSauna(door.inst);
  } else if (selectedGlassPanes().length) {
    const panes = selectedGlassPanes();
    const inst = panes[0].inst, segs = panes.map((p) => p.seg);
    pushHistory();
    inst.glass = glassPanesOf(inst).filter((g) => !segs.includes(g));
    if (!inst.glass.length) delete inst.glass;
    dropFeature(state, 'glass');
    regenerateSauna(inst);
  } else if (selectedOpenings().length) {
    const opens = selectedOpenings();
    const inst = opens[0].inst, segs = opens.map((o) => o.seg);
    pushHistory();
    inst.openings = openingsOf(inst).filter((o) => !segs.includes(o));
    if (!inst.openings.length) delete inst.openings;
    dropFeature(state, 'opening');
    regenerateSauna(inst);
  } else {
    return false;
  }
  renderProperties();
  invalidate();
  return true;
}

/** Delete what is selected on paper: a schematic object, else an unlocked
 *  viewport. The model selection underneath is never touched from a sheet. */
export function deleteSheetSelection(): void {
  if (titleEditor.titleEdit) { SPACES.title.del(); return; }
  const sh = activeSheet();
  if (sh) deleteSchematicSelected(sh);
}

/** Whether `deleteSheetSelection` has anything to take on paper. */
export function sheetSelectionDeletable(): boolean {
  if (titleEditor.titleEdit) return SPACES.title.deletable();
  const sh = activeSheet();
  return !!sh && deletableSheetObjects(sh, SHEET_OBJECTS, schemSelSet).length > 0;
}

/** Whether `deleteSelected` has anything to take in the model — the same list it
 *  walks, so the toolbar button, the panel's Delete row and the key agree. */
export function modelSelectionDeletable(): boolean {
  return !!((state.tool === 'section-3d' && state.drawing.section3d) || selectedFixtureNow() || selectedSupportNow() || selectedDoor() || selectedWallFeature() || selectedGlassPanes().length || selectedOpenings().length || selectedFloor()
    || editModeDeletes() || featureOf(state, 'heaterWall') || featureOf(state, 'innerWall') || featureOf(state, 'bench')
    || state.selectedViewTitle || deleteTargets(state, OBJECTS).length > 0 || selectedConstraint());
}

/** The underlay being edited: Delete removes it and leaves the mode (EDIT_LIFE). */
export function deleteEditedUnderlay(): boolean {
  const bg = editedBg();
  if (bg) {
    pushHistory();
    state.drawing.backgroundImages = bgImages().filter((b) => b.id !== bg.id);
  }
  leaveEdit('underlay');
  renderProperties();
  invalidate();
  return true;
}

/** The imported plan selected to move: Delete removes it and leaves the mode (EDIT_LIFE). */
export function deleteImportedPlan(): boolean {
  if (hasBgGeom()) {
    pushHistory();
    state.drawing.background = null;
    state.drawing.backgroundOffset = undefined;
    bumpBackground();
  }
  leaveEdit('plan');
  renderProperties();
  invalidate();
  return true;
}

export function deleteSelected(): void {
  deleteSelectedNow();
  // A group whose other members went dissolves (inside the same undo step).
  if (!titleEditor.titleEdit && !activeSheet()) normalizeGroups(state.drawing);
}

function deleteSelectedNow(): void {
  // On a sheet (or in its title-block editor) Delete acts on paper — Edit ▸ Delete
  // used to do nothing there, since it only knew the model selection.
  if (titleEditor.titleEdit || activeSheet()) { deleteSheetSelection(); return; }
  // The 3D section tool: Delete removes the section.
  if (state.tool === 'section-3d' && state.drawing.section3d) { deleteSection3d(); return; }
  const sc = selectedConstraint();
  if (sc) { deleteConstraint(sc.id); return; }
  // A view's title alone: Delete hides it.
  if (state.selectedViewTitle) { deleteViewTitle(); return; }
  if (deleteSelectedRoomFeature()) return;
  // A flooring selected alone: Delete removes it and lets go of its drain's
  // picks (with other objects, it goes with them below).
  if (selectedFloor() && deleteTargets(state, OBJECTS).length === 1) { deleteSelectedFloor(); return; }
  // What is edited in place may have a Delete of its own (EDIT_LIFE): the
  // underlay, the imported plan, a room's picked corner.
  if (editModeDelete()) return;
  // A selected hidden heater wall: Delete removes just that wall from its room.
  if (featureOf(state, 'heaterWall')) {
    const room = state.drawing.instances.find((i) => i.id === featureOf(state, 'heaterWall')!.instId);
    if (room) {
      pushHistory();
      removeHeaterWall(room, featureOf(state, 'heaterWall')!.wallId);
      regenerateSauna(room);
    }
    dropFeature(state, 'heaterWall', 'innerWall');
    renderProperties();
    invalidate();
    return;
  }
  // A selected interior partition: Delete removes just that wall from its room.
  if (featureOf(state, 'innerWall')) {
    const room = state.drawing.instances.find((i) => i.id === featureOf(state, 'innerWall')!.instId);
    if (room) {
      pushHistory();
      removeInnerWall(room, featureOf(state, 'innerWall')!.wallId);
      regenerateSauna(room);
    }
    dropFeature(state, 'innerWall');
    renderProperties();
    invalidate();
    return;
  }
  // A selected bench: Delete removes just that bench from its room, as the Bench
  // tool's click does (`removeRoomBench`).
  const bench = featureOf(state, 'bench');
  if (bench) {
    const room = state.drawing.instances.find((i) => i.id === bench.instId);
    if (room) { pushHistory(); removeRoomBench(room, bench.benchId); }
    dropFeature(state, 'bench', 'heaterWall', 'innerWall');
    renderProperties();
    invalidate();
    return;
  }
  // Every selected object (selection.ts `deleteTargets`): THE delete, its
  // cascade and all — a room's fixtures, the connections and dimensions on them.
  const ids = deleteTargets(state, OBJECTS);
  if (ids.length) deleteIds(ids);
}

/** Remove dimension lines anchored to the given instances or drawn shape. */
/** Drop the reflected ceiling plans of rooms no longer in the drawing: every
 *  delete of a room calls it, inside its own undo step. */
function pruneCeilingPlans(): void {
  const d = state.drawing;
  if (d.ceilingPlans?.length) d.ceilingPlans = d.ceilingPlans.filter((v) => d.instances.some((i) => i.id === v.roomId));
}

/** Drop the dimension lines snapped to any of `ids` — a part's point, a
 *  polygon's or a wall's / line's vertex (`anchorObjectId`): a wall's were kept
 *  before, pointing at nothing. */
function pruneDimensionsTo(ids: ReadonlySet<string>): void {
  state.drawing.dimensions = state.drawing.dimensions.filter((d) => ![d.aRef, d.bRef].some((r) => r && ids.has(anchorObjectId(r))));
}
